import React, { useState, useEffect, useMemo, useRef } from "react";
import { useConfig } from "@peracto/peracto-config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxFull } from "@fortawesome/pro-regular-svg-icons/faBoxFull";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, connectHits, Configure } from "react-instantsearch-dom";

import isEmpty from "lodash/isEmpty";
import { formatPrice } from "../utils";
import cn from "classnames";

import styled from "@emotion/styled";

const ScrollbarStyles = styled.div`
    ::-webkit-scrollbar {
        height: 0;
        /* height: 20px; */
    }

    /* 4px scroll thumb. 10px margin from right margin */
    /* ::-webkit-scrollbar-track {
        box-shadow: inset 0 0 15px 15px #000;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-thumb {
        box-shadow: inset 0 0 15px 15px #333;
        border-top: solid 15px transparent;
    }

    ::-webkit-scrollbar-button {
        display: none;
    } */
`;

// Attributes that don't start with 'attributes.*'
const UNPREFIXED_ATTRIBUTES = ["sku", "product_type"];

let Icon = () => (
    <div className="flex items-center justify-center w-full py-10">
        <FontAwesomeIcon icon={faBoxFull} size="6x" />
    </div>
);

const ProductCard = ({
    product,
    isEditMode,
    attributes,
    attributeOperator,
    border,
    index,
}) => {
    const price =
        product?.fromPrices?.price?.inc ||
        product?.prices?.cheapestPrice?.inc ||
        product?.prices?.price?.inc ||
        0;

    const name = product?.attributes?.product_name;
    const availableVariants = product?.variants;

    const image = product?.resources?.find(
        (resource) => resource?.type === "image"
    )?.location;

    return (
        <div
            data-testid="product-card"
            className="product-card max-w-[270px] min-w-[270px] lg:max-w-[360px] lg:min-w-[360px] flex h-full flex-col justify-between rounded-[5px] gap-[15px] bg-brand-white"
        >
            <div>
                <a
                    data-testid="product-card__link"
                    href={`/products/${product.slug}`}
                    onClick={(e) => {
                        if (isEditMode) {
                            e.preventDefault();
                        }
                    }}
                    className="relative flex aspect-square flex-col gap-y-[10px] object-scale-down bg-brand-light-grey rounded-[10px] object-center"
                >
                    <img
                        data-testid="product-card__image"
                        src={image || "/images/placeholder.png"}
                        alt={name}
                        className="h-full w-full rounded-[10px] object-contain"
                    />
                </a>
            </div>

            <div className="flex h-full flex-col">
                <div className="mb-[15px] flex flex-col gap-y-[15px]">
                    <h5
                        data-testid="product-card__name"
                        className={cn(
                            "mb-0 !text-[24px] !leading-[30px] !font-bold text-brand-black flex-grow"
                        )}
                    >
                        {name}
                    </h5>

                    <div>
                        {price && (
                            <div className="flex flex-col items-start gap-x-[10px]">
                                <span
                                    data-testid="product-card__now-price"
                                    className="flex flex-row items-center gap-[10px]"
                                >
                                    <span className="!text-[24px] !leading-[30px] !font-bold">
                                        {availableVariants ? "From " : "Price "}
                                    </span>
                                    <span
                                        className={cn(
                                            "!text-[24px] !leading-[30px] !font-bold"
                                        )}
                                    >
                                        {formatPrice(price)}
                                    </span>
                                    {/* <TaxMessage incTax={incTax} /> */}
                                </span>
                                <span className="text-base">
                                    or £XX.XX with 12 monthly instalments
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const Hits = (props) => {
    const { hits, isEditMode, fallbackImage, border } = props;

    const inStorefront = !window?.location?.pathname.includes("/content/edit/");

    return hits.length > 0 ? (
        <>
            {hits.map((hit, index) => (
                <ProductCard
                    key={hit?.slug || `hit_${index}`}
                    product={hit}
                    isEditMode={isEditMode}
                    fallbackImage={fallbackImage}
                    border={border}
                    index={index}
                />
            ))}
        </>
    ) : isEditMode || !inStorefront ? (
        <Icon />
    ) : null;
};
const CustomHits = connectHits(Hits);

const ProductsContent = ({
    numberOfProducts = 4,
    attributes = [],
    attributeOperator = "and",
    border,
    isEditMode,
}) => {
    const config = useConfig();
    const algolia = config.get("algolia") || {};

    const { applicationId, searchApiKey, indexName } = algolia;

    const searchClient = algoliasearch(applicationId, searchApiKey);

    const [facetFilters, setFacetFilters] = useState();

    useEffect(() => {
        if (attributes?.length > 0) {
            const categories = attributes?.reduce(
                (acc, { attribute, value }) => {
                    const attributeKey = UNPREFIXED_ATTRIBUTES.includes(
                        attribute
                    )
                        ? attribute
                        : `attributes.${attribute}`;
                    if (value.length > 0) acc.push(`${attributeKey}:${value}`);

                    return acc;
                },
                []
            );

            setFacetFilters(categories);
        }
    }, [attributes]);

    if (isEmpty(algolia)) {
        console.error(
            "No Algolia configuration defined. Please refer to the Peracto documentation."
        );
    }

    return useMemo(
        () =>
            isEmpty(algolia) ? null : (
                <InstantSearch
                    searchClient={searchClient}
                    indexName={indexName}
                >
                    <Configure
                        hitsPerPage={numberOfProducts}
                        facetFilters={
                            facetFilters
                                ? attributeOperator === "and"
                                    ? facetFilters
                                    : [facetFilters]
                                : null
                        }
                    />

                    <CustomHits isEditMode={isEditMode} border={border} />
                </InstantSearch>
            ),
        [
            algolia,
            searchClient,
            indexName,
            numberOfProducts,
            border,
            facetFilters,
            attributeOperator,
            isEditMode,
        ]
    );
};

const ProductsRenderer = ({ state, ...props }) => {
    const { title = "Products" } = state;
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    const relatedProductsRef = useRef(null);

    const scroll = (direction) => {
        if (relatedProductsRef.current) {
            const { current } = relatedProductsRef;
            const gap = 28;
            let scrollAmount = 330 + gap;
            const product = document.querySelector(
                '[data-testid="product-relations__item-0"]'
            );

            if (product) {
                scrollAmount = product.clientWidth + gap;
            }

            if (direction === "left") {
                current.scrollLeft -= scrollAmount;
            } else {
                current.scrollLeft += scrollAmount;
            }
        }
    };

    const checkScrollPosition = () => {
        if (relatedProductsRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } =
                relatedProductsRef.current;

            setIsAtStart(scrollLeft <= 0);
            setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
        }
    };

    useEffect(() => {
        checkScrollPosition();
        const scrollDiv = relatedProductsRef.current;

        if (scrollDiv) {
            scrollDiv.addEventListener("scroll", checkScrollPosition);
            window.addEventListener("resize", checkScrollPosition);
            return () => {
                scrollDiv.removeEventListener("scroll", checkScrollPosition);
                window.removeEventListener("resize", checkScrollPosition);
            };
        }
    }, []);

    return (
        <div>
            <div className="flex flex-col items-center justify-between md:flex-row mb-[30px]">
                <h2
                    data-testid="product-relations__title"
                    className={
                        "text-center !text-[30px] !leading-[34px] text-brand-black !lg:text-[40px] !lg:leading-[46px] !mb-0"
                    }
                >
                    {title}
                </h2>

                <div className="hidden gap-[10px] md:flex items-center">
                    <button
                        className={cn(
                            `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                            {
                                "cursor-not-allowed opacity-25": isAtStart,
                            }
                        )}
                        onClick={() => scroll("left")}
                        disabled={isAtStart}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                        className={cn(
                            `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                            {
                                "cursor-not-allowed opacity-25": isAtEnd,
                            }
                        )}
                        onClick={() => scroll("right")}
                        disabled={isAtEnd}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>

            <ScrollbarStyles
                ref={relatedProductsRef}
                className="flex scroll-smooth flex-row gap-[30px] flex-nowrap overflow-auto product-card-container content__products"
            >
                <ProductsContent {...state} {...props} />
            </ScrollbarStyles>

            <div className="flex justify-center gap-[10px] md:hidden mt-[15px] items-center">
                <button
                    className={cn(
                        `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                        {
                            "cursor-not-allowed opacity-25": isAtStart,
                        }
                    )}
                    onClick={() => scroll("left")}
                    disabled={isAtStart}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                    className={cn(
                        `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
                        {
                            "cursor-not-allowed opacity-25": isAtEnd,
                        }
                    )}
                    onClick={() => scroll("right")}
                    disabled={isAtEnd}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
        </div>
    );
};

export default ProductsRenderer;
